import { useState, useRef, useCallback, useEffect } from "react"

import { GripVertical } from "lucide-react"

interface ResizablePanelProps {
    leftPanel: React.ReactNode
    rightPanel: React.ReactNode
    defaultLeftWidth?: number
    minLeftWidth?: number
    maxLeftWidth?: number
}
export function ResizablePanel({ leftPanel, rightPanel, defaultLeftWidth = 300, minLeftWidth = 200, maxLeftWidth = 600 }: Readonly<ResizablePanelProps>) {
    const [leftWidth, setLeftWidth] = useState(defaultLeftWidth)
    const [isDragging, setIsDragging] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        setIsDragging(true)
    }, [])

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !containerRef.current) return

            const containerRect = containerRef.current.getBoundingClientRect()
            const newLeftWidth = e.clientX - containerRect.left

            const constrainedWidth = Math.min(Math.max(newLeftWidth, minLeftWidth), maxLeftWidth)
            setLeftWidth(constrainedWidth)
        },
        [isDragging, minLeftWidth, maxLeftWidth]
    )

    const handleMouseUp = useCallback(() => {
        setIsDragging(false)
    }, [])

    useEffect(() => {
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove)
            document.addEventListener("mouseup", handleMouseUp)
            document.body.style.cursor = "col-resize"
            document.body.style.userSelect = "none"
        } else {
            document.removeEventListener("mousemove", handleMouseMove)
            document.removeEventListener("mouseup", handleMouseUp)
            document.body.style.cursor = ""
            document.body.style.userSelect = ""
        }

        return () => {
            document.removeEventListener("mousemove", handleMouseMove)
            document.removeEventListener("mouseup", handleMouseUp)
            document.body.style.cursor = ""
            document.body.style.userSelect = ""
        }
    }, [isDragging, handleMouseMove, handleMouseUp])

    return (
        <div ref={containerRef} className="flex flex-row h-full w-full">
            <div style={{ width: leftWidth }} className="flex-shrink-0 h-full overflow-hidden">
                {leftPanel}
            </div>

            <div className={`flex-shrink-0 w-1 bg-gray-200 hover:bg-gray-300 cursor-col-resize relative group transition-colors duration-150 ${isDragging ? "bg-blue-400" : ""}`} onMouseDown={handleMouseDown}>
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                    <GripVertical className="w-3 h-3 text-gray-500" />
                </div>
                <div className="absolute inset-y-0 -left-1 -right-1 cursor-col-resize" />
            </div>

            <div className="flex-1 h-full overflow-hidden">{rightPanel}</div>
        </div>
    )
}
